import React from "react";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import moment from "moment";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Table, Filter, FilterValues, PageHeader, Card, FilterTypes } from "@cofano/cck";
import { IconButton, Tooltip, LinearProgress, Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import VoyageSelect from "components/selects/VoyageSelect";
import { TableId, useBackendTable } from "components/table";
import Helmet from "components/helmet/Helmet";
import CommentsWidget from "components/comments/CommentsWidget";
import useComments from "components/comments/useComments";
import CommentIndicatorButton from "components/comments/CommentIndicatorButton";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import StatusSelect from "view/statuses/components/selects/StatusSelect";
import BulkStatusChanger from "view/statuses/components/changer/BulkStatusChanger";
import FleetSelect, { FleetSelectOption } from "view/fleets/components/selects/FleetSelect";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import StatusChanger from "view/statuses/components/changer/StatusChanger";

import { DataFormat, hasPluginPermission, Language, usePermission } from "imports/Utils";

import { DossierLink, LocationLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";

import CallProgressSelect from "./components/selects/CallProgressSelect";
import CallProductsPopover from "./components/popover/CallProductsPopover";

import { GetCallsDocument, GetCallsQuery } from "__generated__/view/calls/graphql/call.queries.generated";
import { Constraint, CommentEntityType, StatusType } from "__generated__/enums";

import { ChildPropsFromPagination } from "types/commons";

type CallFromQuery = ChildPropsFromPagination<GetCallsQuery, "calls">

const Calls = () => {

	const navigate = useNavigate();
	const client = useApolloClient();
	const { isAdmin, isCompanyUser, isOperator, isCustomer, isMainUserLimited, isTerminal, isFinance, isReadOnly, isSkipper } = usePermission();

	const session = useStoreState(state => state.session);
	const { statuses } = session;

	const { tableProps, filterProps, filterValuesProps, refetch } = useBackendTable({
		id: TableId.Calls,
		selectSettings: {
			isFullSelectEnabled: false,
		},
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetCallsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort,
						},
						search: {
							ship: filter.ship?.map((it: ShipSelectOption) => it.id),
							voyage: filter.voyage?.id,
							fleets: filter.fleets?.map((f: FleetSelectOption) => f.id),
							location: filter.location?.map((it: LocationSelectOption) => it.id),
							status: filter.status?.id,
							constraint: filter.constraint,
							plannedBetween: filter.plannedBetween?.[1] ? {
								from: filter.plannedBetween[0],
								to: filter.plannedBetween[1],
							} : undefined,
						},
					},
				});

				callback({
					content: result.data.calls?.content ?? [],
					totalResults: result.data.calls?.totalElements ?? 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const { commentsProps, openComment } = useComments({
		calls: tableProps.content as CallFromQuery[],
		onChange: () => refetch(),
	});

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("CALLS")}
					<Helmet title={intl.formatMessage({ id: "CALLS" })} />
				</PageHeader.Title>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "ship",
								title: intl.formatMessage({ id: "SHIPS" }),
								renderFilterValue: (value) => value.map((option: ShipSelectOption) => option.name).join(", "),
							}, {
								id: "voyage",
								title: intl.formatMessage({ id: "VOYAGE" }),
								renderFilterValue: (value) => value?.voyageNumber,
							}, {
								id: "fleets",
								title: intl.formatMessage({ id: "FLEETS" }),
								renderFilterValue: (value) => value.map((option: FleetSelectOption) => option.description).join(", "),
							}, {
								id: "location",
								title: intl.formatMessage({ id: "LOCATION" }),
								renderFilterValue: (value) => value.map((option: LocationSelectOption) => option.description).join(", "),
							}, {
								id: "status",
								title: intl.formatMessage({ id: "STATUS" }),
								renderFilterValue: (value) => value?.description,
							}, {
								id: "plannedBetween",
								title: intl.formatMessage({ id: "PTA_BETWEEN" }),
								renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
							}, {
								id: "constraint",
								title: intl.formatMessage({ id: "PROGRESS" }),
								renderFilterValue: (value) => value.map((option: Constraint) => intl.formatMessage({ id: option })).join(", "),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={!isTerminal ? (row) => {
						const call = row as CallFromQuery;
						navigate(`/view/voyages/${call.voyage.id}/details?tab=details`, { state: { selectedCallId: call.id } });
					} : undefined}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={[ {
								id: "ship",
								title: intl.formatMessage({ id: "VOYAGES_SHIP" }),
								disabled: !(isAdmin || isCompanyUser || isMainUserLimited || isCustomer || isOperator || isFinance),
								render: (value, onChange) => <ShipSelect multiple={true} value={value} onChange={onChange} />,
							}, {
								id: "voyage",
								title: intl.formatMessage({ id: "VOYAGE" }),
								render: (value, onChange) => <VoyageSelect value={value} onChange={onChange} />,
							}, {
								id: "fleets",
								title: intl.formatMessage({ id: "FLEETS" }),
								render: (value, onChange) => <FleetSelect multiple={true} value={value} onChange={onChange} />,
							}, {
								id: "location",
								title: intl.formatMessage({ id: "LOCATION" }),
								render: (value, onChange) => <LocationSelect value={value} onChange={onChange} multiple />,
							}, {
								id: "status",
								title: intl.formatMessage({ id: "STATUS" }),
								render: (value, onChange) => <StatusSelect value={value} onChange={onChange} statuses={statuses?.CALL ?? []} />,
							}, {
								id: "plannedBetween",
								title: intl.formatMessage({ id: "PTA_BETWEEN" }),
								filterType: FilterTypes.DATE_RANGE,
							}, {
								id: "constraint",
								title: intl.formatMessage({ id: "PROGRESS" }),
								render: (value, onChange) => <CallProgressSelect value={value} onChange={onChange} multiple />,
								disabled: isSkipper,
							} ]} />
					)}
					columns={[ {
						id: "ship",
						title: intl.formatMessage({ id: "VOYAGES_SHIP" }),
						render: (row: CallFromQuery) => <ShipLink ship={row.voyage?.ship} />,
						width: 180,
						filterComponent: isAdmin || isCompanyUser || isMainUserLimited || isCustomer || isOperator || isFinance
							? (value, onChange) => (
								<ShipSelect
									label={intl.formatMessage({ id: "VOYAGES_SHIP" })}
									value={value}
									onChange={options => onChange(options)}
									multiple />
							) : undefined,
					}, {
						id: "voyage",
						title: intl.formatMessage({ id: "VOYAGE" }),
						render: (row: CallFromQuery) => <VoyageLink voyage={row.voyage} />,
						width: 180,
						filterComponent: (value, onChange) => (
							<VoyageSelect
								label={intl.formatMessage({ id: "VOYAGE" })}
								value={value}
								onChange={onChange} />
						),
					}, {
						id: "charterNumber",
						title: intl.formatMessage({ id: "CHARTER" }),
						width: 150,
						render: (row: CallFromQuery) => <DossierLink charter={row.voyage.cargos[0]?.charter} />,
					}, {
						id: "voyage.creditor",
						title: intl.formatMessage({ id: "CREDITOR" }),
						sortable: true,
						width: 200,
						render: (row: CallFromQuery) => <RelationLink relation={row.voyage.creditor} />,
						disabled: isSkipper,
					}, {
						id: "debtor",
						title: intl.formatMessage({ id: "DEBTOR" }),
						width: 200,
						render: (row: CallFromQuery) => <RelationLink relation={row.voyage.cargos[0]?.charter?.debtor} />,
						disabled: isSkipper,
					}, {
						id: "location",
						title: intl.formatMessage({ id: "LOCATION" }),
						render: (row: CallFromQuery) => <LocationLink location={row.location} />,
						width: 200,
						filterComponent: (value, onChange) => (
							<LocationSelect
								label={intl.formatMessage({ id: "LOCATION" })}
								value={value}
								onChange={onChange}
								multiple />
						),
					}, {
						id: "from",
						title: intl.formatMessage({ id: "FROM" }),
						render: (row: CallFromQuery) => <LocationLink location={row.voyage.from} />,
						width: 200,
						disabled: isSkipper,
					}, {
						id: "to",
						title: intl.formatMessage({ id: "TO" }),
						render: (row: CallFromQuery) => <LocationLink location={row.voyage.to} />,
						width: 200,
						disabled: isSkipper,
					}, {
						id: "pta",
						title: intl.formatMessage({ id: "PTA" }),
						render: (row: CallFromQuery) => <FormattedDate date={row.pta} withoutYear />,
						sortable: true,
					}, {
						id: "ptd",
						title: intl.formatMessage({ id: "PTD" }),
						render: (row: CallFromQuery) => <FormattedDate date={row.ptd} withoutYear />,
						sortable: true,
					}, {
						id: "systemEta",
						title: intl.formatMessage({ id: "CALLS_ETA" }),
						render: (row: CallFromQuery) => {
							const eta = row.eta || row.systemEta;
							const pta = moment(row.pta);
							const convertedEta = moment(eta);
							const symbol = ((row.pta && eta) && (row.pta > eta)) ? "-" : "+";

							return eta ? (
								<span style={{ alignItems: "center", display: "flex" }}>
									<FormattedDate date={eta} withoutYear />

									<Tooltip title={`${symbol} ${pta.from(convertedEta, true)}`}>
										<span>
											<IconButton size="small">
												<InfoOutlinedIcon fontSize="small" />
											</IconButton>
										</span>
									</Tooltip>
								</span>
							) : (
								<React.Fragment />
							);
						},
						sortable: true,
						width: 120,
					}, {
						id: "etd",
						title: intl.formatMessage({ id: "CALLS_ETD" }),
						render: (row: CallFromQuery) => <FormattedDate date={row.etd} withoutYear />,
						sortable: true,
					}, {
						id: "ata",
						title: intl.formatMessage({ id: "CALLS_ATA" }),
						render: (row: CallFromQuery) => <FormattedDate date={row.ata} withoutYear />,
						sortable: true,
					}, {
						id: "productsWithDetails",
						title: intl.formatMessage({ id: "PRODUCTS" }),
						render: (row: CallFromQuery) => <CallProductsPopover productsWithDetails={row.productsWithDetails} />,
						width: 220,
						disabled: isSkipper,
					}, {
						id: "calls.assignedContainers",
						title: intl.formatMessage({ id: "ROTATION_CALL_ASSIGNED_CONTAINERS" }),
						render: (row: CallFromQuery) => (row.nrLoadingCargos || 0) + (row.nrDischargingCargos || 0),
						disabled: !hasPluginPermission(session, "useContainers"),
					}, {
						id: "expectedLoadingAmount",
						title: intl.formatMessage({ id: "ROTATION_CALL_DISCHARGING_AMOUNT" }),
						disabled: !hasPluginPermission(session, "useContainers"),
					}, {
						id: "loadedWeight",
						title: intl.formatMessage({ id: "LOADING_WEIGHT" }),
						render: (row: CallFromQuery) => DataFormat.tonnage(row.loadedWeight),
						disabled: !hasPluginPermission(session, "useContainers"),
						width: 150,
					}, {
						id: "callLoadingTeu ",
						title: intl.formatMessage({ id: "LOADING_TEU" }),
						render: (row: CallFromQuery) => DataFormat.tonnage(row.callLoadingTeu ),
						disabled: !hasPluginPermission(session, "useContainers"),
						width: 150,
					}, {
						id: "expectedDischargingAmount",
						title: intl.formatMessage({ id: "ROTATION_CALL_LOADING_AMOUNT" }),
						disabled: !hasPluginPermission(session, "useContainers"),
					}, {
						id: "dischargedWeight",
						title: intl.formatMessage({ id: "DISCHARGING_WEIGHT" }),
						render: (row: CallFromQuery) => DataFormat.tonnage(row.dischargedWeight),
						disabled: !hasPluginPermission(session, "useContainers"),
						width: 150,
					}, {
						id: "callDischargingTeu",
						title: intl.formatMessage({ id: "DISCHARGING_TEU" }),
						render: (row: CallFromQuery) => DataFormat.tonnage(row.callDischargingTeu),
						disabled: !hasPluginPermission(session, "useContainers"),
						width: 150,
					}, {
						id: "weight",
						title: intl.formatMessage({ id: "WEIGHT" }),
						render: (row: CallFromQuery) => DataFormat.tonnage(row.weight),
						disabled: hasPluginPermission(session, "useContainers"),
					}, {
						id: "volume",
						title: intl.formatMessage({ id: "CARGO_VOLUME" }),
						render: (row: CallFromQuery) => DataFormat.volumeM3(row.volume),
						disabled: hasPluginPermission(session, "useContainers"),
					}, {
						id: "status",
						title: intl.formatMessage({ id: "STATUS" }),
						render: (row: CallFromQuery) => {
							return (
								<StatusChanger
									session={session}
									entity={row}
									statusType={StatusType.Call}
									refetch={refetch}
									showHistory
								/>
							);
						},
						width: 200,
						sortable: true,
						sortKey: "lastStatusChange.status.description",
						filterComponent: (value, onChange) => (
							<StatusSelect
								label={intl.formatMessage({ id: "STATUS" })}
								value={value}
								onChange={onChange}
								statuses={statuses?.CALL ?? []} />
						),
					}, {
						id: "constraint",
						title: intl.formatMessage({ id: "PROGRESS" }),
						render: (row: CallFromQuery) => {
							if (row.atso) {
								const start = row.atso;
								const end = row.ateo || row.atd || row.etd || row.ptd;

								let current = moment().unix() * 1000;
								let error = false;
								let percentage = 0;

								if (end) {
									current = moment().unix() * 1000;

									if (row.atd && end > row.atd) {
										percentage = 100;
									} else if (current > end) {
										percentage = 100;
										error = true;
									} else {
										percentage = (((current) - start) / (end - start)) * 100;
										if (percentage > 100) {
											percentage = 100;
										}
									}
								} else {
									error = true;
								}

								return (
									<div style={{ alignItems: "center", display: "flex" }}>
										<div style={{ flexGrow: 1 }}>
											<LinearProgress
												color={error ? "error" : "primary"}
												variant="determinate"
												value={percentage} />
										</div>

										{percentage > 0 && !error && (
											<small style={{ marginLeft: 4 }}>{DataFormat.percentage(percentage)}</small>
										)}
									</div>
								);
							} else {
								return intl.formatMessage({ id: "UNKNOWN" });
							}
						},
						filterComponent: (value, onChange) => (
							<CallProgressSelect
								label={intl.formatMessage({ id: "PROGRESS" })}
								value={value}
								onChange={onChange}
								multiple />
						),
						width: 200,
						disabled: isSkipper,
					} ]}
					actions={[ {
						render: (selectedRows) =>
							<BulkStatusChanger
								statusType={StatusType.Call}
								entities={selectedRows || []}
								refetch={refetch}
							/>,
					} ]}
					rowActions={isReadOnly ? undefined : [ {
						icon: <React.Fragment />,
						onClick: () => {},
						render: (row) => (
							<CommentIndicatorButton
								entity={row}
								onClick={() => openComment(row.id, CommentEntityType.Call)}
								session={session} />
						),
					}, {
						icon: <React.Fragment />,
						onClick: () => {},
						hidden: (row: CallFromQuery) => !row.hasReefer || !hasPluginPermission(session, "reeferChecks"),
						render: (row: CallFromQuery) => (
							<Tooltip title={intl.formatMessage({ id: "REEFER" })}>
								<IconButton
									size='small'
									onClick={() =>  navigate(`/view/voyages/${row.voyage?.id}/details?tab=reefer`)}>
									<InsertDriveFileIcon color={row.hasInvalidReefer ? "error" : "inherit"} />
								</IconButton>
							</Tooltip>
						),
					} ]} />
			</Card>

			<CommentsWidget {...commentsProps} />
		</div>
	);
};

export default Calls;
